import React, { useState } from "react";
import { AppShell } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@jem-open/jem-ui";
import SideNavbar from "./components/SideNavbar";
import MobileTopBar from "./components/MobileTopBar";
import MobileSideNavDrawer from "./components/MobileSideNavDrawer";
import DesktopOnlyBanner from "./components/DesktopOnlyBanner";
import useMobileAvailability from "./components/useMobileAvailability";
import AuthProvider from "../providers/AuthProvider";
import AnalyticsProvider from "../providers/AnalyticsProvider";
import { SSEProvider } from "../providers/sse/SSEProvider";
import { NotificationsProvider as CustomNotificationsProvider } from "../providers/notifications/NotificationsProvider";
import { SnackbarsProvider } from "../providers/notifications/SnackbarsProvider";
import FeatureFlagProvider, {
  useEnabledFeatureFlag,
} from "../common/feature-flagging/FeatureFlagProvider";
import {
  AIInsightsChatProvider,
  AIInsightsFloatingButton,
  AIInsightsChatSidebar,
} from "../features/ai-insights";
import FeatureFlags from "../common/feature-flagging/FeatureFlags";
import {
  MigrationStatusProvider,
  MigrationBanner,
  MigrationRedirect,
  ReadOnlyModal,
} from "../common/migration";

// Inner component to access FeatureFlag context
const AppLayoutContent: React.FC = () => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobileAvailable } = useMobileAvailability();
  // Mantine's Navbar publishes `--mantine-navbar-width` globally even when
  // hidden, leaving a sliver of left padding on the AppShell main. Skipping
  // the navbar entirely on phone removes the variable and clears the gap.
  const isPhone = useMediaQuery("(max-width: 767px)");

  // Check if AI Insights feature is enabled via Flagsmith
  const showAIInsights = useEnabledFeatureFlag(FeatureFlags.AI_INSIGHTS);

  // Home owns its own banner decision (it depends on the T&A feature being
  // enabled). For every other non-mobile route we surface the banner here.
  const showDesktopOnlyBanner =
    !isMobileAvailable &&
    location.pathname !== "/" &&
    location.pathname !== "/home";

  return (
    <AppShell
      navbarOffsetBreakpoint="sm"
      asideOffsetBreakpoint="sm"
      padding={0}
      fixed
      navbar={isPhone ? undefined : <SideNavbar />}
    >
      <CustomNotificationsProvider>
        <SSEProvider>
          <SnackbarsProvider>
            <AIInsightsChatProvider>
              {/* Cut-off first: a redirected employer should never see the old chrome render
                  behind the interstitial, and the banner is redundant once they are leaving. */}
              <MigrationRedirect />
              <MigrationBanner />
              <MobileTopBar
                onMenuClick={() => setMobileNavOpen(true)}
                onHomeClick={() => navigate("/")}
              />
              {showDesktopOnlyBanner && (
                <div className="px-sm sm:hidden">
                  <DesktopOnlyBanner />
                </div>
              )}
              <Outlet />
              <MobileSideNavDrawer
                opened={mobileNavOpen}
                onClose={() => setMobileNavOpen(false)}
              />
              {/* AI Insights Chat Components */}
              {showAIInsights && (
                <>
                  <AIInsightsFloatingButton />
                  <AIInsightsChatSidebar />
                </>
              )}
              <ReadOnlyModal />
              <Toaster position="bottom-right" />
            </AIInsightsChatProvider>
          </SnackbarsProvider>
        </SSEProvider>
      </CustomNotificationsProvider>
    </AppShell>
  );
};

const AppLayout: React.FC = () => (
  <AuthProvider>
    <FeatureFlagProvider>
      <AnalyticsProvider>
        <MigrationStatusProvider>
          <AppLayoutContent />
        </MigrationStatusProvider>
      </AnalyticsProvider>
    </FeatureFlagProvider>
  </AuthProvider>
);

export default AppLayout;
